import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus,
  FunnelSimple,
  CircleNotch,
  Download,
  Receipt,
  Trash
} from '@phosphor-icons/react';


function InvoiceStatusBadge({ status }) {
  const styles = {
    'Draft': 'bg-slate-100 text-slate-700',
    'Sent': 'bg-blue-100 text-blue-700',
    'Paid': 'bg-green-100 text-green-700',
    'Overdue': 'bg-red-100 text-red-700',
    'Cancelled': 'bg-slate-100 text-slate-500',
  };

  const labels = {
    'Draft': 'Borrador',
    'Sent': 'Enviada',
    'Paid': 'Pagada',
    'Overdue': 'Vencida',
    'Cancelled': 'Cancelada',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-slate-100 text-slate-700'}`}
          data-testid={`invoice-status-${status}`}>
      {labels[status] || status}
    </span>
  );
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/api/invoices${params}`, {
        withCredentials: true
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (e, invoiceId) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${invoiceId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `factura-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handleDeleteInvoice = async (e, invoiceId) => {
    e.stopPropagation();
    if (!window.confirm('Eliminar esta factura?')) return;
    try {
      await axios.delete(`${API_URL}/api/invoices/${invoiceId}`, { withCredentials: true });
      fetchInvoices();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar factura');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelectedIds(prev => prev.size === invoices.length ? new Set() : new Set(invoices.map(i => i.id)));
  };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedIds.size} facturas seleccionadas?`)) return;
    try {
      await axios.post(`${API_URL}/api/bulk/delete-invoices`, { ids: [...selectedIds] }, { withCredentials: true });
      setSelectedIds(new Set());
      fetchInvoices();
    } catch (e) { alert('Error al eliminar'); }
  };

  const totalPending = invoices
    .filter(i => i.status === 'Sent')
    .reduce((sum, i) => sum + (i.total - (i.amount_paid || 0)), 0);

  const totalPaid = invoices
    .filter(i => i.status === 'Paid')
    .reduce((sum, i) => sum + i.total, 0);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Facturas</h1>
          <p className="text-slate-500">Gestiona tus facturas y cobranzas</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium" data-testid="bulk-delete-invoices">
              <Trash size={18} /> Eliminar ({selectedIds.size})
            </button>
          )}
          <Link to="/invoices/new" className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-testid="new-invoice-button">
            <Plus size={20} weight="bold" /> Nueva Factura
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Total Facturas</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="total-invoices-count">{invoices.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Por Cobrar</p>
          <p className="text-2xl font-bold text-amber-600" data-testid="total-pending">${totalPending.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Cobrado</p>
          <p className="text-2xl font-bold text-green-600" data-testid="total-paid">${totalPaid.toLocaleString()}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-4">
          <FunnelSimple size={20} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            data-testid="invoice-status-filter"
          >
            <option value="">Todos los estados</option>
            <option value="Draft">Borrador</option>
            <option value="Sent">Enviada</option>
            <option value="Paid">Pagada</option>
            <option value="Overdue">Vencida</option>
            <option value="Cancelled">Cancelada</option>
          </select>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="invoices-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-4 w-10">
                    <input type="checkbox" checked={selectedIds.size === invoices.length && invoices.length > 0} onChange={toggleAll} className="w-4 h-4 accent-blue-600" data-testid="select-all-invoices" />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Numero
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Cliente
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Vencimiento
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Total
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    className={`table-row-hover cursor-pointer ${selectedIds.has(invoice.id) ? 'bg-blue-50' : ''}`}
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <td className="px-3 py-4 w-10">
                      <input type="checkbox" checked={selectedIds.has(invoice.id)} onChange={() => toggleSelect(invoice.id)} onClick={e => e.stopPropagation()} className="w-4 h-4 accent-blue-600" />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Receipt size={18} className="text-slate-400" />
                        <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900">{invoice.client_name}</p>
                        <p className="text-sm text-slate-500">{invoice.client_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(invoice.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {new Date(invoice.due_date).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">
                        ${invoice.total?.toLocaleString()}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <InvoiceStatusBadge status={invoice.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => handleDownloadPdf(e, invoice.id)}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
                          title="Descargar PDF"
                          data-testid={`download-invoice-pdf-${invoice.id}`}
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={(e) => handleDeleteInvoice(e, invoice.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors text-slate-400 hover:text-red-600"
                          title="Eliminar factura"
                          data-testid={`delete-invoice-${invoice.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No se encontraron facturas</p>
            <Link
              to="/invoices/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              data-testid="create-first-invoice-button"
            >
              <Plus size={18} />
              Crear primera factura
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
